import {
    Box,
    Button,
    Chip,
    CircularProgress,
    IconButton,
    Divider,
    InputAdornment,
    MuiIcons,
    Paper,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    Typography,
} from "@leap4/frontend";
import React, { useCallback, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import { urlService } from "../../routing/urlsService";
import {
    useGetDashboardCatalogue,
    useGetDashboardPacing,
    useGetDashboardTimeseries,
    useGetDashboardWindow,
} from "../../../api/generated/api";
import type {
    GetDashboardTimeseriesBasis,
    GetDashboardTimeseriesGrain,
    GetDashboardWindowBasis,
    GetDashboardWindowUnit,
} from "../../../api/generated/models";
import BreakdownTable from "./breakdownTable";
import DashboardChart, { ISelection } from "./dashboardChart";
import DateRange from "./dateRange";
import GrainStatus from "./grainStatus";
import {
    addDaysUtc,
    addHoursIso,
    bucketToInstant,
    daysBetweenUtc,
    hoursBetweenIso,
    previousWindow,
    unitLabel,
} from "./format";
import KpiRow from "./kpiRow";
import MetricPicker from "./metricPicker";
import PacingStrip from "./pacingStrip";
import PayoutGate from "./payoutGate";
import DashboardTabs from "./tabs";
import CampaignSelect, { DEFAULT_CAMPAIGN, useCampaign } from "./useCampaign";
import { useStickyRange } from "./useStickyRange";

const { Dashboard: DashboardIcon, KeyboardReturn, Refresh } = MuiIcons;

/** Shared query behaviour. */
const QUERY_OPTS = {
    keepPreviousData: true,
    staleTime: 120000,
    cacheTime: 900000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
} as const;
const GRAINS: GetDashboardTimeseriesGrain[] = ["auto", "hour", "day", "week"];

/** The Overall dashboard: KPI row, chart, publisher breakdown. */
interface IProps {
    /** Scopes every panel to one publisher, for the workspace. Undefined is the whole campaign. */
    publisher?: string;
    /** The workspace supplies its own heading and back link. */
    embedded?: boolean;
}

const Dashboard = ({ publisher, embedded }: IProps = {}): JSX.Element => {
    const history = useHistory();
    const [campaign, setCampaign] = useCampaign();
    const [startDate, endDate, applyRange] = useStickyRange("overall");
    const [basis, setBasis] = useState<GetDashboardTimeseriesBasis | null>(null);
    const [grain, setGrain] = useState<GetDashboardTimeseriesGrain>("auto");
    const [metrics, setMetrics] = useState<string[] | null>(null);
    const [dimension, setDimension] = useState<string | null>(null);
    // Blank means "use the campaign's payout"; the field shows it so the override starts honest.
    // Typed into as a draft — committing per keystroke re-ran every query on the way to "4.50".
    const [payout, setPayout] = useState<string>("");
    const [payoutDraft, setPayoutDraft] = useState<string>("");
    const [selection, setSelection] = useState<ISelection | null>(null);

    const onCampaignChange = useCallback(
        (next: string) => {
            setPayout("");
            setPayoutDraft("");
            setCampaign(next);
        },
        [setCampaign]
    );

    const catalogueQuery = useGetDashboardCatalogue(
        { campaign },
        { query: { ...QUERY_OPTS, staleTime: 600000 } }
    );
    const catalogue = catalogueQuery.data;

    // A session what-if counts: it values a conversion just as the campaign's own payout would.
    const effectivePayout =
        payout !== "" && Number.isFinite(Number(payout))
            ? Number(payout)
            : catalogue?.payoutPerConversion ?? null;

    // The backend decides which modes are usable and which to open on.
    const activeBasis: GetDashboardTimeseriesBasis =
        basis ?? ((catalogue?.defaultBasis as GetDashboardTimeseriesBasis) || "real");

    const modes = useMemo(
        () =>
            (catalogue?.modes ?? []).map((m) => ({
                ...m,
                available: m.available || (effectivePayout !== null && m.availableWithPayout),
            })),
        [catalogue, effectivePayout]
    );

    const activeMode = modes.find((m) => m.basis === activeBasis);

    // Null until picked, so the backend's default selection stays authoritative on first load.
    const activeMetrics = metrics ?? catalogue?.defaultMetrics?.[activeBasis] ?? [];

    const timeseriesQuery = useGetDashboardTimeseries(
        {
            campaign,
            basis: activeBasis,
            from: startDate,
            to: endDate,
            grain,
            ...(activeMetrics.length > 0 ? { metric: activeMetrics } : {}),
            ...(publisher ? { publisher } : {}),
            ...(payout !== "" ? { payout } : {}),
        },
        { query: { ...QUERY_OPTS, enabled: !!catalogue } }
    );
    const timeseries = timeseriesQuery.data;

    /** The chart selection narrows every panel below it. */
    const window = useMemo(() => {
        if (!selection || !timeseries) return { from: startDate, to: endDate, selected: false };
        const from = selection.from.slice(0, 10);
        // A week bucket is keyed by its Monday, so its bounds widen to the whole week.
        const to =
            timeseries.grain === "week"
                ? addDaysUtc(selection.to.slice(0, 10), 6)
                : selection.to.slice(0, 10);
        return { from, to, selected: true };
    }, [selection, timeseries, startDate, endDate]);

    /** An hourly selection is bounded by instants, not dates — otherwise picking 15:00 silently */
    const hourBounds = useMemo(() => {
        if (!selection || timeseries?.grain !== "hour") return null;
        const fromTs = bucketToInstant(selection.from);
        const toTs = addHoursIso(bucketToInstant(selection.to), 1);
        const span = hoursBetweenIso(fromTs, toTs);
        return {
            fromTs,
            toTs,
            baseFromTs: addHoursIso(fromTs, -span),
            baseToTs: fromTs,
            span,
        };
    }, [selection, timeseries?.grain]);

    const baseline = useMemo(
        () => (window.selected ? previousWindow(window.from, window.to) : null),
        [window]
    );

    // KPI row and breakdown share a window, a baseline and a dimension, so they share one request.
    const windowQuery = useGetDashboardWindow(
        {
            campaign,
            basis: activeBasis as GetDashboardWindowBasis,
            from: window.from,
            to: window.to,
            unit: (timeseries?.grain ?? "day") as GetDashboardWindowUnit,
            ...(dimension ? { dimension } : {}),
            ...(publisher ? { publisher } : {}),
            ...(payout !== "" ? { payout } : {}),
            ...(baseline ? { baseFrom: baseline.baseFrom, baseTo: baseline.baseTo } : {}),
            ...(hourBounds
                ? {
                      fromTs: hourBounds.fromTs,
                      toTs: hourBounds.toTs,
                      baseFromTs: hourBounds.baseFromTs,
                      baseToTs: hourBounds.baseToTs,
                  }
                : {}),
        },
        { query: { ...QUERY_OPTS, enabled: !!catalogue } }
    );

    // Independent of the range and the chart selection: always today against yesterday.
    // No basis: the strip is identical on all three, so switching must not refetch it.
    const pacingQuery = useGetDashboardPacing(
        {
            campaign,
            ...(publisher ? { publisher } : {}),
            ...(payout !== "" ? { payout } : {}),
        },
        { query: { ...QUERY_OPTS, enabled: !!catalogue } }
    );

    const busy =
        timeseriesQuery.isFetching || windowQuery.isFetching || pacingQuery.isFetching;

    // The catalogue too: payout and mode availability can change from the campaign form.
    const refreshAll = useCallback(() => {
        catalogueQuery.refetch();
        timeseriesQuery.refetch();
        windowQuery.refetch();
        pacingQuery.refetch();
    }, [catalogueQuery, timeseriesQuery, windowQuery, pacingQuery]);
    const payoutDirty = payoutDraft !== payout;

    const activeDimension = dimension ?? catalogue?.breakdownDimension.key ?? "";

    /** Open the segment in Explorer, carrying the window so the drill lands on what was on screen. */
    const drillToExplorer = useCallback(
        (dimValue: string) => {
            const p = new URLSearchParams({
                from: window.from,
                to: window.to,
                basis: activeBasis,
                analyzeBy: activeDimension,
                exactDimension: activeDimension,
                exactValue: dimValue,
                minImpressions: "0",
            });
            if (publisher) p.append("publisher", publisher);
            if (campaign !== DEFAULT_CAMPAIGN) p.set("campaign", campaign);
            history.push(`${urlService.dashboardExplorer()}?${p.toString()}`);
        },
        [history, window.from, window.to, activeBasis, activeDimension, publisher, campaign]
    );

    const onRangeChange = useCallback(
        (from: string, to: string) => {
            // A new range invalidates a selection made against the old bucket space.
            setSelection(null);
            applyRange(from, to);
        },
        [applyRange]
    );

    const windowLabel = window.selected
        ? selection && timeseries?.grain === "hour"
            ? `${selection.from} → ${selection.to} UTC`
            : `${window.from} → ${window.to}`
        : "Entire visible range";

    // Short enough to sit on one line inside a card; the exact bounds live in the tooltip.
    const compareUnit = hourBounds ? "hour" : timeseries?.grain === "week" ? "week" : "day";
    const baselineDays = baseline ? daysBetweenUtc(baseline.baseFrom, baseline.baseTo) : 0;
    const compareCount = hourBounds
        ? hourBounds.span
        : compareUnit === "week"
        ? Math.max(1, Math.round(baselineDays / 7))
        : baselineDays;
    const baselineLabel = baseline
        ? `vs preceding ${unitLabel(compareCount, compareUnit)}`
        : null;
    const windowUnitNote = window.selected
        ? timeseries?.grain === "week"
            ? compareCount === 1
                ? "one full week"
                : `${compareCount} full weeks`
            : null
        : null;

    const baselineTitle = baseline
        ? hourBounds
            ? `Compared against ${hourBounds.baseFromTs.slice(0, 16).replace("T", " ")} to ` +
              `${hourBounds.baseToTs.slice(0, 16).replace("T", " ")} UTC, the ` +
              `${unitLabel(compareCount, compareUnit)} immediately before the selected window`
            : `Compared against ${baseline.baseFrom} to ${baseline.baseTo}, the ` +
              `${unitLabel(compareCount, compareUnit)} immediately before the selected window`
        : null;

    if (catalogueQuery.isLoading) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", py: 10 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (catalogueQuery.isError || !catalogue) {
        return (
            <Paper variant="outlined" sx={{ p: 4, m: 2 }}>
                <Typography variant="h6">Dashboard unavailable</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    The dashboard catalogue could not be loaded, so there is nothing reliable to show.
                </Typography>
                <Button sx={{ mt: 2 }} variant="outlined" onClick={() => catalogueQuery.refetch()}>
                    Try again
                </Button>
            </Paper>
        );
    }

    return (
        <Box sx={{ p: 2 }}>
            {!embedded && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap", mb: 1 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <DashboardIcon color="primary" />
                        <Typography variant="h6">Overall</Typography>
                    </Box>
                    <DashboardTabs active="overall" />
                    <CampaignSelect campaign={campaign} onChange={onCampaignChange} />
                </Box>
            )}

            <PayoutGate campaign={campaign} payout={effectivePayout} onUseForSession={(v) => {
                    setPayoutDraft(v);
                    setPayout(v);
                }}>
            {/* Controls left, Payout and Metrics pinned right. The loading slot between them takes
                the slack, so its text can grow without shifting anything. */}
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    flexWrap: "nowrap",
                    mb: 2,
                }}
            >
                <ToggleButtonGroup
                    size="small"
                    exclusive
                    value={activeBasis}
                    sx={{ flexShrink: 0 }}
                    onChange={(_e, v) => v && setBasis(v as GetDashboardTimeseriesBasis)}
                >
                    {modes.map((m) => (
                        <ToggleButton
                            key={m.basis}
                            value={m.basis}
                            disabled={!m.available || busy}
                            // An unavailable mode says why rather than just refusing to click.
                            title={m.available ? undefined : m.reason ?? undefined}
                        >
                            {m.label}
                        </ToggleButton>
                    ))}
                </ToggleButtonGroup>

                <DateRange from={startDate} to={endDate} onApply={onRangeChange} disabled={busy} />

                <ToggleButtonGroup
                    size="small"
                    exclusive
                    value={grain}
                    sx={{ flexShrink: 0 }}
                    onChange={(_e, v) => v && setGrain(v as GetDashboardTimeseriesGrain)}
                >
                    {GRAINS.map((g) => (
                        <ToggleButton key={g} value={g} disabled={busy} sx={{ textTransform: "capitalize" }}>
                            {g}
                        </ToggleButton>
                    ))}
                </ToggleButtonGroup>

                {/* Fixed slot: "→ day" only shows on Auto, and must not move the row when it does. */}
                <Box
                    sx={{
                        width: 52,
                        flexShrink: 0,
                        fontSize: 12,
                        color: "text.disabled",
                        whiteSpace: "nowrap",
                    }}
                >
                    {timeseries && grain === "auto" ? `→ ${timeseries.grain}` : ""}
                </Box>

                <Box sx={{ flex: 1, minWidth: 0, overflow: "hidden" }}>
                    <GrainStatus loading={busy} />
                </Box>

                {/* Spinner in place of the icon, so a second click has nothing to hit. */}
                <Tooltip arrow title={busy ? "Loading…" : "Reload every panel"}>
                    <span>
                        <IconButton
                            size="small"
                            onClick={refreshAll}
                            disabled={busy}
                            sx={{ flexShrink: 0 }}
                        >
                            {busy ? <CircularProgress size={18} /> : <Refresh sx={{ fontSize: 20 }} />}
                        </IconButton>
                    </span>
                </Tooltip>

                <Tooltip
                    arrow
                    title={
                        `US dollars earned per conversion, the average the customer states. A ` +
                        `what-if for this session only — it does not change the campaign's saved ` +
                        `payout, and nobody else sees it. Blank uses ` +
                        (catalogue.payoutPerConversion === null
                            ? `the campaign's payout, which nobody has set yet.`
                            : `the campaign's payout.`)
                    }
                >
                    <TextField
                        size="small"
                        type="number"
                        label="Payout / conv."
                        value={payoutDraft}
                        placeholder={
                            catalogue.payoutPerConversion === null
                                ? ""
                                : catalogue.payoutPerConversion.toFixed(2)
                        }
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                            setPayoutDraft(e.target.value)
                        }
                        onBlur={() => setPayout(payoutDraft)}
                        onKeyDown={(e: React.KeyboardEvent) => {
                            if (e.key === "Enter") setPayout(payoutDraft);
                        }}
                        InputLabelProps={{ shrink: true }}
                        disabled={busy}
                        InputProps={{
                            startAdornment: <InputAdornment position="start">$</InputAdornment>,
                            // Only while the typed value differs from what the figures were built from.
                            endAdornment: payoutDirty ? (
                                <InputAdornment position="end">
                                    <Tooltip arrow title="Press Enter to apply">
                                        <KeyboardReturn sx={{ fontSize: 15, color: "primary.main" }} />
                                    </Tooltip>
                                </InputAdornment>
                            ) : null,
                        }}
                        inputProps={{ step: 1, min: 0 }}
                        sx={{ width: 160, flexShrink: 0 }}
                    />
                </Tooltip>

                <Box sx={{ flexShrink: 0 }}>
                    <MetricPicker
                        metrics={catalogue.metrics}
                        selected={activeMetrics}
                        max={catalogue.maxSelectedMetrics}
                        onChange={setMetrics}
                    />
                </Box>
            </Box>

            {activeMode && !activeMode.available && activeMode.reason && (
                <Paper variant="outlined" sx={{ p: 1.5, mb: 2, borderColor: "warning.main" }}>
                    <Typography variant="body2" color="warning.main">
                        {activeMode.label} basis is unavailable: {activeMode.reason}
                    </Typography>
                </Paper>
            )}

            <Box sx={{ mb: 2.5 }}>
                <PacingStrip pacing={pacingQuery.data} loading={pacingQuery.isFetching} />
            </Box>

            {/* Above: how is today going. Below: how did the selected window do. */}
            <Divider sx={{ mb: 2.5 }} />

            <Box
                sx={{
                    display: "flex",
                    alignItems: "baseline",
                    gap: 1,
                    mb: 1.5,
                    flexWrap: "wrap",
                }}
            >
                <Tooltip
                    arrow
                    title="Everything below is measured over this window. Click a bucket or drag across the chart to narrow it."
                >
                    <Typography variant="subtitle2" sx={{ cursor: "help" }}>
                        Window: {windowLabel}
                    </Typography>
                </Tooltip>
                <Typography variant="caption" sx={{ color: "text.disabled" }}>
                    {window.selected
                        ? `${windowUnitNote ? `${windowUnitNote}, ` : ""}selected on the chart — ` +
                          "click it again to go back to the full range"
                        : "the whole visible range; click or drag the chart to narrow it"}
                </Typography>
            </Box>

            <Box sx={{ mb: 2 }}>
                <KpiRow
                    kpis={windowQuery.data?.kpis ?? []}
                    baselineLabel={baselineLabel}
                    baselineTitle={baselineTitle}
                    loading={windowQuery.isFetching}
                />
            </Box>

            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                    <Typography variant="subtitle2">Overall performance over time</Typography>
                    {/* Hourly scans are slow enough to need a visible loading state. */}
                    {timeseriesQuery.isFetching && <CircularProgress size={14} />}
                    {activeBasis === "predicted" && (catalogue.forecastDays ?? 0) > 0 && (
                        <Typography variant="caption" sx={{ color: "text.disabled" }}>
                            dashed = forecast, {catalogue.forecastDays} days ahead
                        </Typography>
                    )}
                    <Typography variant="caption" sx={{ ml: "auto", color: "text.disabled" }}>
                        Click or drag on the chart to select a period
                    </Typography>
                </Box>

                {timeseriesQuery.isLoading ? (
                    <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
                        <CircularProgress />
                    </Box>
                ) : timeseriesQuery.isError ? (
                    <Typography variant="body2" color="error" sx={{ py: 6, textAlign: "center" }}>
                        The chart could not be loaded for this range.
                    </Typography>
                ) : (
                    <DashboardChart
                        buckets={timeseries?.buckets ?? []}
                        series={timeseries?.series ?? []}
                        axes={timeseries?.axes ?? []}
                        grain={timeseries?.grain ?? "day"}
                        selection={selection}
                        onSelect={setSelection}
                        onClear={() => setSelection(null)}
                        loading={timeseriesQuery.isFetching}
                    />
                )}
            </Paper>

            <Paper variant="outlined" sx={{ p: 2 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1, flexWrap: "wrap" }}>
                    <Typography variant="subtitle2">Breakdown — {windowLabel}</Typography>
                    {windowQuery.isFetching && <CircularProgress size={14} />}
                    {windowQuery.data?.breakdown?.deltaMode && (
                        <Typography variant="caption" sx={{ color: "text.disabled" }}>
                            Δ and contribution columns compare against the preceding{" "}
                            {unitLabel(compareCount, compareUnit)}
                        </Typography>
                    )}
                </Box>

                <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", mb: 1.5 }}>
                    {catalogue.breakdownDimensions.map((d) => (
                        <Chip
                            key={d.key}
                            size="small"
                            label={d.label}
                            title={d.tier === "raw" ? "No pre-aggregated source — this one is slow." : undefined}
                            color={activeDimension === d.key ? "primary" : "default"}
                            variant={activeDimension === d.key ? "filled" : "outlined"}
                            onClick={() => setDimension(d.key)}
                        />
                    ))}
                </Box>

                <BreakdownTable
                    breakdown={windowQuery.data?.breakdown}
                    baselineTitle={baselineTitle}
                    conversionLabel={activeMode?.conversionLabel ?? "Conversions"}
                    rpmLabel={activeMode?.rpmLabel ?? "RPM"}
                    loading={windowQuery.isFetching}
                    refresh={() => windowQuery.refetch()}
                    onRowClick={drillToExplorer}
                />
            </Paper>
            </PayoutGate>
        </Box>
    );
};

export default Dashboard;
